import type { Card, CardSide } from '../types'
import type { AppTranslations } from '../i18n/app'
import { displaySides, sideKindLabel } from './cardSideDisplay'
import { cardTranslation, hasDefinition, hasTranslation } from './cardFilters'
import { valueSide, type ValueKind } from './fieldValue'

type CardEditorLabels = AppTranslations['cardEditor']

/**
 * One row of the desktop left panel, as data (brief §22, amended for the
 * fixed-position redesign).
 *
 * The row used to print every side-B field as a labelled chip, in stored
 * position order — so a deck of otherwise-similar cards repeated
 * "Translation / Definition / Example" on every row, and the label was the
 * only thing telling two differently-ordered cards apart. This version gives
 * the four core kinds (term — handled by the caller, not here — definition,
 * translation, example) FIXED POSITIONS instead: position alone identifies
 * the field, so the label is dropped. Everything else (image, audio, and any
 * custom-named field) is not text at all — it is a glyph the caller renders,
 * grouped into `extras`.
 *
 * Two behaviours changed from the old `fields`/`gaps` shape, both because the
 * old logic under-served exactly the fixed slots this version has to get
 * right:
 *
 *  - `translation` is read through `cardTranslation()` (ADR-050), not off
 *    `card.sides` directly. ADR-050 retired `Translation`-kind sides from
 *    system decks — the resolved value now lives only in
 *    `CardResponse.translation` — so a side-only reading dashes translation on
 *    every system-deck card, even ones with a real curated translation Learn
 *    displays. `cardTranslation()` is the same ADR-050-aware resolver every
 *    other surface already uses.
 *  - `definition`/`example` read only the AUTHORITATIVE (first, ADR-066) side
 *    of their kind. The old dash check was `fields.some(kind matches && has
 *    content)` — content on a *second* Definition side cleared the dash even
 *    though ADR-066 says only the first field is what Learn reads. A card
 *    whose authoritative field is blank is dashed exactly like an absent one,
 *    regardless of what a later repeat contains.
 *
 * Dashes remain scoped to exactly the ADR-064 pair (translation, definition) —
 * six placeholders per row would bury the two that decide anything. Example
 * absence is not dashed; it is simply omitted.
 */
export interface ListRowCoreField {
  /** The stored side's id, to focus on click — `ActiveVocabCard`'s own draft
   * key (`sideToDraft`). `null` when there is nothing to click into: a
   * curated (ADR-050) translation with no authored side of its own. */
  key: string | null
  content: string
  /** Total sides of this kind on the card. >1 shows a count badge — ADR-066:
   * repeats are counted, never printed. */
  count: number
}

/** Everything outside the four core kinds. Built-in, fixed-label kinds
 * (image, audio, link, dialog) get their own bucket and their own icon —
 * their label is not something an author chose, so an icon can stand in for
 * it. `note` (Custom) and any unrecognized/legacy kind — a label IS
 * something an author chose there, so no icon can name it — share one
 * generic bucket instead. */
export type ExtraGlyphKind = 'image' | 'audio' | 'link' | 'dialog' | 'custom'

export interface ListRowExtra {
  kind: ExtraGlyphKind
  /** First non-blank side in this bucket — the click target. */
  key: string
  count: number
  /** `image` bucket only: that side's content, for the thumbnail. */
  imageSrc?: string
  /** Distinct display names in this bucket, for the hover title — the only
   * way a `custom` glyph's name is ever shown, per the task's own framing
   * ("a custom label cannot be identified by an icon"). */
  names: string[]
}

/** A field the card does not have (or has blank), shown as a dash. Carries
 * the kind so the row can place it in the right fixed slot. */
export interface ListRowGap {
  kind: 'translation' | 'definition'
}

export interface ListRow {
  definition: ListRowCoreField | null
  translation: ListRowCoreField | null
  example: ListRowCoreField | null
  extras: ListRowExtra[]
  /** ADR-064: no non-blank Definition or Translation, so this card does not
   * enter Learn. Equivalent to `gaps.length === 2`, stated separately because
   * it is the fact the row's not-in-study mark is about. */
  notInStudy: boolean
  gaps: ListRowGap[]
}

const EXTRA_BUCKET: Record<string, ExtraGlyphKind> = {
  image: 'image', audio: 'audio', link: 'link', dialog: 'dialog',
}

function oneLine(content: string): string {
  return content.replace(/\s+/g, ' ').trim()
}

/** The side holding `kind`'s value (ADR-104: the first non-blank one), plus
 * how many sides of that kind exist in total. `null` when the card has no
 * value of the kind, blank sides included — a blank field is not the card's
 * definition or example, and does not hide a later one that is. */
function authoritativeOfKind(
  sides: ReturnType<typeof displaySides>, kind: ValueKind,
): { side: CardSide; count: number } | null {
  const matches = sides.filter((s) => (s.side.kind ?? s.side.label) === kind)
  const value = valueSide(matches.map((m) => m.side), kind)
  if (!value) return null
  return { side: value, count: matches.length }
}

export function listRow(card: Card, ce: CardEditorLabels): ListRow {
  const sides = displaySides(card)

  const definitionSide = authoritativeOfKind(sides, 'definition')
  const definitionContent = definitionSide ? oneLine(definitionSide.side.content) : ''
  const definition: ListRowCoreField | null = hasDefinition(card)
    ? { key: definitionSide!.side.id, content: definitionContent, count: definitionSide!.count }
    : null

  const exampleSide = authoritativeOfKind(sides, 'example')
  const exampleContent = exampleSide ? oneLine(exampleSide.side.content) : ''
  const example: ListRowCoreField | null = exampleContent.length > 0
    ? { key: exampleSide!.side.id, content: exampleContent, count: exampleSide!.count }
    : null

  // ADR-050: the resolved value, not the side — a curated system-deck
  // translation has no `translation`-kind side at all. The side (if any) is
  // still resolved separately, purely to give an authored-but-blank
  // Translation a click target to fill in.
  const translationSideMatches = sides.filter((s) => (s.side.kind ?? s.side.label) === 'translation')
  // The value side when there is one; otherwise the first (blank) field, which
  // is where a click to fill it in lands.
  const translationSide = valueSide(translationSideMatches.map((m) => m.side), 'translation')
    ?? translationSideMatches.find((s) => s.ordinalInKind === 1)?.side
    ?? null
  const translation: ListRowCoreField | null = hasTranslation(card)
    ? {
        key: translationSide?.id ?? null,
        content: (cardTranslation(card) ?? '').trim(),
        count: translationSideMatches.length,
      }
    : null

  const gaps: ListRowGap[] = []
  if (!hasTranslation(card)) gaps.push({ kind: 'translation' })
  if (!hasDefinition(card)) gaps.push({ kind: 'definition' })

  // Extras: every non-blank side outside the three core kinds above (term is
  // already excluded by `displaySides`), grouped by visual bucket.
  const extraSides = sides.filter((s) => {
    const kind = s.side.kind ?? s.side.label
    return kind !== 'definition' && kind !== 'translation' && kind !== 'example'
      && s.side.content.trim().length > 0
  })
  const bucketOrder: ExtraGlyphKind[] = ['image', 'audio', 'link', 'dialog', 'custom']
  const extras: ListRowExtra[] = bucketOrder
    .map((bucketKind): ListRowExtra | null => {
      const inBucket = extraSides.filter((s) => (EXTRA_BUCKET[s.side.kind ?? ''] ?? 'custom') === bucketKind)
      if (inBucket.length === 0) return null
      const first = inBucket[0].side
      const names = Array.from(new Set(inBucket.map((s) => sideKindLabel(s.side, ce))))
      return {
        kind: bucketKind,
        key: first.id,
        count: inBucket.length,
        imageSrc: bucketKind === 'image' ? first.content : undefined,
        names,
      }
    })
    .filter((e): e is ListRowExtra => e !== null)

  return { definition, translation, example, extras, gaps, notInStudy: gaps.length === 2 }
}
